import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Input,
  Text,
  Card,
  Badge,
  Modal,
  SlideOver,
  Table,
  Toggle,
} from '@teals/ui-web';
import type { Column } from '@teals/ui-web';

interface SchemaField {
  name: string;
  type: string;
  searchable: boolean;
}

interface AssociatedAgent {
  id: string;
  name: string;
}

interface DataSource {
  id: string;
  name: string;
  type: 'Algolia';
  indexName: string;
  recordCount: number;
  schema: SchemaField[];
  agents: AssociatedAgent[];
}

const MOCK_DATA_SOURCES: DataSource[] = [
  {
    id: 'ds_1a2b3c4d',
    name: 'Job Listings',
    type: 'Algolia',
    indexName: 'prod_jobs',
    recordCount: 24_850,
    schema: [
      { name: 'title', type: 'string', searchable: true },
      { name: 'company', type: 'string', searchable: true },
      { name: 'location', type: 'string', searchable: true },
      { name: 'salary_min', type: 'number', searchable: false },
      { name: 'salary_max', type: 'number', searchable: false },
      { name: 'posted_at', type: 'date', searchable: false },
    ],
    agents: [
      { id: 'ag-1', name: 'Job Matcher' },
      { id: 'ag-2', name: 'Resume Screener' },
    ],
  },
  {
    id: 'ds_5e6f7g8h',
    name: 'Knowledge Base',
    type: 'Algolia',
    indexName: 'prod_kb_articles',
    recordCount: 3_120,
    schema: [
      { name: 'title', type: 'string', searchable: true },
      { name: 'body', type: 'text', searchable: true },
      { name: 'category', type: 'string', searchable: true },
      { name: 'author', type: 'string', searchable: false },
      { name: 'updated_at', type: 'date', searchable: false },
    ],
    agents: [{ id: 'ag-3', name: 'Support Bot' }],
  },
  {
    id: 'ds_9i0j1k2l',
    name: 'Product Catalog',
    type: 'Algolia',
    indexName: 'prod_products',
    recordCount: 8_430,
    schema: [
      { name: 'name', type: 'string', searchable: true },
      { name: 'description', type: 'text', searchable: true },
      { name: 'sku', type: 'string', searchable: true },
      { name: 'price', type: 'number', searchable: false },
      { name: 'in_stock', type: 'boolean', searchable: false },
    ],
    agents: [
      { id: 'ag-4', name: 'Product Recommender' },
      { id: 'ag-5', name: 'Inventory Agent' },
      { id: 'ag-6', name: 'Customer Support' },
    ],
  },
  {
    id: 'ds_3m4n5o6p',
    name: 'Employee Directory',
    type: 'Algolia',
    indexName: 'prod_employees',
    recordCount: 1_250,
    schema: [
      { name: 'full_name', type: 'string', searchable: true },
      { name: 'email', type: 'string', searchable: true },
      { name: 'department', type: 'string', searchable: true },
      { name: 'role', type: 'string', searchable: true },
      { name: 'hire_date', type: 'date', searchable: false },
    ],
    agents: [{ id: 'ag-7', name: 'HR Assistant' }],
  },
];

const PageWrapper = styled.div`
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const DSCard = styled(Card)`
  cursor: pointer;
  border: 1px solid var(--border);
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    border-color: var(--accent);
    box-shadow: var(--shadow-md);
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

const ModalForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SelectLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const StyledSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg-surface);
  color: var(--text-primary);
  font-size: 0.875rem;
  outline: none;

  &:focus {
    border-color: var(--accent);
  }
`;

const DetailSection = styled.div`
  margin-bottom: 1.5rem;
`;

const DetailLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.375rem;
`;

const DetailValue = styled.div`
  font-size: 0.875rem;
  color: var(--text-primary);
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--border);
  margin: 1.5rem 0;
`;

const AgentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.875rem;
  color: var(--text-primary);

  &:last-child {
    border-bottom: none;
  }
`;

const AddAgentRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const DataSources: React.FC = () => {
  const [dataSources, setDataSources] =
    useState<DataSource[]>(MOCK_DATA_SOURCES);
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<DataSource | null>(null);
  const [newName, setNewName] = useState('');
  const [newIndex, setNewIndex] = useState('');

  const handleAdd = () => {
    if (!newName.trim()) return;
    const ds: DataSource = {
      id: `ds_${Math.random().toString(36).slice(2, 10)}`,
      name: newName,
      type: 'Algolia',
      indexName: newIndex || `prod_${newName.toLowerCase().replace(/\s+/g, '_')}`,
      recordCount: 0,
      schema: [],
      agents: [],
    };
    setDataSources((prev) => [...prev, ds]);
    setNewName('');
    setNewIndex('');
    setAddOpen(false);
  };

  const toggleSearchable = (fieldName: string) => {
    if (!selected) return;
    const updated = {
      ...selected,
      schema: selected.schema.map((f) =>
        f.name === fieldName ? { ...f, searchable: !f.searchable } : f,
      ),
    };
    setSelected(updated);
    setDataSources((prev) =>
      prev.map((ds) => (ds.id === updated.id ? updated : ds)),
    );
  };

  const removeAgent = (agentId: string) => {
    if (!selected) return;
    const updated = {
      ...selected,
      agents: selected.agents.filter((a) => a.id !== agentId),
    };
    setSelected(updated);
    setDataSources((prev) =>
      prev.map((ds) => (ds.id === updated.id ? updated : ds)),
    );
  };

  const schemaColumns: Column<SchemaField>[] = [
    { key: 'name', header: 'Field Name' },
    { key: 'type', header: 'Type' },
    {
      key: 'searchable',
      header: 'Searchable',
      align: 'center',
      render: (_val: boolean, row: SchemaField) => (
        <Toggle
          value={row.searchable}
          onChange={() => toggleSearchable(row.name)}
          size="sm"
        />
      ),
    },
  ];

  return (
    <PageWrapper>
      <Header>
        <Text variant="h2">Data Sources</Text>
        <Button variant="primary" onClick={() => setAddOpen(true)}>
          Add Data Source
        </Button>
      </Header>

      <Grid>
        {dataSources.map((ds) => (
          <DSCard
            key={ds.id}
            padding={20}
            elevation="sm"
            onClick={() => setSelected(ds)}
          >
            <CardTop>
              <Text variant="h4">{ds.name}</Text>
              <Badge variant="info" size="sm">
                {ds.type}
              </Badge>
            </CardTop>
            <CardMeta>
              <MetaItem>
                {ds.recordCount.toLocaleString()} records
              </MetaItem>
              <MetaItem>
                <Badge variant="neutral" size="sm">
                  {ds.agents.length} agent{ds.agents.length !== 1 ? 's' : ''}
                </Badge>
              </MetaItem>
            </CardMeta>
          </DSCard>
        ))}
      </Grid>

      <Modal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add Data Source"
      >
        <ModalForm>
          <Input
            label="Data source name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Product Catalog"
          />
          <SelectWrapper>
            <SelectLabel>Type</SelectLabel>
            <StyledSelect defaultValue="Algolia">
              <option value="Algolia">Algolia</option>
            </StyledSelect>
          </SelectWrapper>
          <Input
            label="Index name"
            value={newIndex}
            onChange={(e) => setNewIndex(e.target.value)}
            placeholder="e.g. prod_products"
          />
          <ModalActions>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAdd}>
              Add Source
            </Button>
          </ModalActions>
        </ModalForm>
      </Modal>

      <SlideOver
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name ?? ''}
        width="lg"
        position="right"
      >
        {selected && (
          <>
            <DetailSection>
              <DetailLabel>Name</DetailLabel>
              <DetailValue>{selected.name}</DetailValue>
            </DetailSection>

            <DetailSection>
              <DetailLabel>Type</DetailLabel>
              <Badge variant="info">{selected.type}</Badge>
            </DetailSection>

            <DetailSection>
              <DetailLabel>Index Name</DetailLabel>
              <DetailValue style={{ fontFamily: 'monospace' }}>
                {selected.indexName}
              </DetailValue>
            </DetailSection>

            <DetailSection>
              <DetailLabel>Records</DetailLabel>
              <DetailValue>
                {selected.recordCount.toLocaleString()}
              </DetailValue>
            </DetailSection>

            <Divider />

            <DetailSection>
              <DetailLabel>Schema</DetailLabel>
              {selected.schema.length > 0 ? (
                <Table
                  columns={schemaColumns}
                  data={selected.schema}
                  rowKey="name"
                />
              ) : (
                <DetailValue style={{ color: 'var(--text-muted)' }}>
                  No schema fields defined
                </DetailValue>
              )}
            </DetailSection>

            <Divider />

            <DetailSection>
              <DetailLabel>Agent Associations</DetailLabel>
              {selected.agents.length === 0 ? (
                <DetailValue style={{ color: 'var(--text-muted)' }}>
                  No agents associated
                </DetailValue>
              ) : (
                selected.agents.map((agent) => (
                  <AgentRow key={agent.id}>
                    <span>{agent.name}</span>
                    <Button
                      variant="ghost"
                      onClick={() => removeAgent(agent.id)}
                    >
                      Remove
                    </Button>
                  </AgentRow>
                ))
              )}
              <AddAgentRow>
                <Button variant="secondary">+ Add Agent</Button>
              </AddAgentRow>
            </DetailSection>
          </>
        )}
      </SlideOver>
    </PageWrapper>
  );
};

export default DataSources;
